import { useContext } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrashAlt } from '@fortawesome/pro-light-svg-icons'

import {
  Button, Form, Line, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import { confirmDelete } from '@functions/entity'

import useCampaignComparable, { useCampaignComparableForm } from '@hooks/useCampaignComparable'
import useCampaignSelector from '@hooks/useCampaignSelector'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import ComparableDetails from './components/ComparableDetails'
import ComparableImage from './components/ComparableImage'
import PricingAndSale from './components/PriceAndSale'
import PropertyDetails from './components/PropertyDetails'
import PropertyFeatures from './components/PropertyFeatures'

const MODAL_KEY = 'CreateOrEditCampaignComparableModal'

type ModalCallbacks = {
  closeModal: () => void,
  createCampaignComparable: (payload: HandleCallbackActionParams) => void,
  updateCampaignComparable: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CreateOrEditCampaignComparableModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const {
    closeModal, createCampaignComparable, updateCampaignComparable,
  } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const campaignComparable = digObject(modalPayload, 'campaignComparable', {})

  const newRecord = !campaignComparable.id

  const {
    callbacks: {
      createCampaignComparable: createFn,
      deleteCampaignComparable: deleteFn,
      updateCampaignComparable: updateFn,
    },
    creating,
    deleting,
    updating,
  } = useCampaignComparable(campaignComparable)

  const campaignComparableFormPayload = useCampaignComparableForm(campaignComparable, { validateOn: 'change' })

  const {
    entityState,
    entityState: {
      external_link,
      notes,
    },
    handlers,
    saveEnabled,
  } = campaignComparableFormPayload

  const { agency: { geo_country_id }, campaign } = useCampaignSelector()

  const createOrUpdateCampaignComparablePayload = {
    callbacks: {
      action: newRecord ? createFn : updateFn,
      afterAction: closeModal,
    },
    entityParams: { ...entityState, campaign_id: campaign.id },
    toastText: `Comparable ${newRecord ? 'Created' : 'Updated'}`,
  }

  const confirmDeleteParams = {
    callbacks: {
      afterAction: closeModal,
      deleteFn,
    },
    strings: {
      entityName: 'Comparable',
    },
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header
        callbacks={callbacks}
        title={newRecord ? 'Create' : 'Edit'}
        titleSecondLine="Comparable"
      />

      <SidebarModal.Content>
        <Form>
          {!newRecord && (
            <ComparableImage campaignComparable={campaignComparable} />
          )}

          <PropertyDetails
            campaignComparable={campaignComparable}
            campaignComparableFormPayload={campaignComparableFormPayload}
            geoCountryId={geo_country_id}
          />

          <PropertyFeatures campaignComparableFormPayload={campaignComparableFormPayload} />

          <ComparableDetails campaignComparableFormPayload={campaignComparableFormPayload} />

          <PricingAndSale campaignComparableFormPayload={campaignComparableFormPayload} />

          <Line />

          <Form.Field label="External Link">
            <input type="text" name="external_link" value={external_link} {...handlers} />
          </Form.Field>

          <Form.Field label="Notes" marginTop="large">
            {/*
             // @ts-expect-error TS2322 */}
            <textarea name="notes" value={notes} {...handlers} style={{ height: 80, resize: 'vertical' }} />
          </Form.Field>

          {!newRecord && (
            <Form.Field label="More Options" marginTop="large">
              <Button
                buttonStyle="secondaryUtility"
                icon={<FontAwesomeIcon icon={faTrashAlt} />}
                loading={deleting}
                onClick={() => confirmDelete(confirmDeleteParams)}
                size="medium"
              >
                Delete Comparable
              </Button>
            </Form.Field>
          )}
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          loadingBubbleColor="white"
          onClick={
            newRecord
              ? () => createCampaignComparable(createOrUpdateCampaignComparablePayload)
              : () => updateCampaignComparable(createOrUpdateCampaignComparablePayload)
          }
          size="large"
        >
          {saveEnabled ? `${newRecord ? 'Create' : 'Update'} Comparable` : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditCampaignComparableModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
